import React, { forwardRef, JSX, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';

import Papa from 'papaparse';

import { Dialog } from '@components/shared/Dialog/Dialog';
import { Loader } from '@components/shared/Loader/Loader';
import {
  mutateBranchBatch,
  mutateClientBatch,
  mutateJobTypeBatch,
  mutatePurchaseOrderBatch,
  mutateRequisitionBatch,
  mutateSiteBatch,
} from '@hooks/apiHooks';
import { BranchModel } from '@models/branch.model';
import { ClientModel } from '@models/client.model';
import { PurchaseOrderModel } from '@models/purchaseOrder.model';
import { RegionModel } from '@models/region.model';
import { JobTypeModel, RequisitionModel } from '@models/requisition.model';
import { SiteModel } from '@models/site.model';
import { snackbarSlice } from '@store/slices/snackbar';
import { useQueryClient } from '@tanstack/react-query';

import {
  branchFieldMapper,
  clientFieldMapper,
  jobTypeFieldMapper,
  MasterDataType,
  parseNestValidationErrors,
  purchaseOrderFieldMapper,
  requisitionFieldMapper,
  siteFieldMapper,
  toSafeRow,
  validationSchema,
} from '../../masterDataHelpers';
import { SelectedRowType } from '../../MasterDataSettings';

import MasterDataTable from './MasterDataTable';
type Props = {
  acitveTab: MasterDataType;
  modalOpen: boolean;
  dependencies: {
    jobTypes: JobTypeModel[];
    sites: SiteModel[];
    clients: ClientModel[];
    purchaseOrders: PurchaseOrderModel[];
    requisitions: RequisitionModel[];
    regions: RegionModel[];
    branches: BranchModel[];
  };
  setModalOpen: (open: boolean) => void;
};
const mutateDataBatch = {
  branch: mutateBranchBatch,
  site: mutateSiteBatch,
  requisition: mutateRequisitionBatch,
  purchaseOrder: mutatePurchaseOrderBatch,
  client: mutateClientBatch,
  jobType: mutateJobTypeBatch,
};
const filedMappers = {
  branch: branchFieldMapper,
  site: siteFieldMapper,
  requisition: requisitionFieldMapper,
  purchaseOrder: purchaseOrderFieldMapper,
  client: clientFieldMapper,
  jobType: jobTypeFieldMapper,
};
function MasterDataImportRender<T extends SelectedRowType>(
  { acitveTab, modalOpen, dependencies, setModalOpen }: Props,
  ref: any,
) {
  useImperativeHandle(ref, () => ({
    handleImport,
  }));
  const [importData, setImportedData] = useState<T[]>();
  const [isLoadingData, setIsLoadingData] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutateAsync } = mutateDataBatch[acitveTab](queryClient, (data: any) => {
    let msg;
    if (!data.response?.data?.error) {
      dispatch(
        snackbarSlice.actions.showSuccess({
          message: `${acitveTab} was imported successfully `,
        }),
      );
      setModalOpen(false);
    }
    if (data?.response?.data?.error?.data?.validationErrors) {
      const { message } = data.response.data.error.data;
      if (message) {
        msg = message;
      }
      const fieldErrors = parseNestValidationErrors(data.response.data.error.data.validationErrors);

      dispatch(
        snackbarSlice.actions.showError({
          message: msg || JSON.stringify(fieldErrors) || `Server Error`,
        }),
      );
    } else if (data.response?.data?.error) {
      dispatch(
        snackbarSlice.actions.showError({
          message: `Server Error`,
        }),
      );
    }
  });
  const handleImport = async (file: File) => {
    const reader = new FileReader();
    setIsLoadingData(true);
    reader.onload = async (event) => {
      try {
        const csvText = event.target?.result as string;
        const results = Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header: any) => header.toLowerCase(),
        });

        if (results.errors.length > 0) {
          throw new Error('Error parsing CSV: ' + results.errors.map((e) => e.message).join(', '));
        }

        const rawRows: Record<string, any>[] = results.data as Record<string, any>[];
        const errors: { [key: number]: { field: string; message: string }[] } = {};

        const normalizedRows = rawRows.map((row) =>
          toSafeRow(
            Object.fromEntries(Object.entries(row).map(([key, value]) => [key.toLowerCase(), value])),
            filedMappers[acitveTab],
            dependencies,
          ),
        );
        // Validate all rows in parallel
        const validationResults = await Promise.allSettled(
          normalizedRows.map((row) => {
            if (row.error.length > 0) {
              errors[rawRows.indexOf(row) + 2] = row.error;
              return Promise.reject({ errors: row.error });
            }
            return validationSchema[acitveTab].validate(row.result, { abortEarly: false });
          }),
        );

        const validatedData: T[] = [];
        validationResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            validatedData.push(result.value as any);
          } else {
            errors[index + 2] = result.reason.errors;
          }
        });
        setImportedData(validatedData);
        if (Object.keys(errors).length > 0) {
          alert(
            `Import completed with ${validatedData.length} valid rows. ${Object.keys(errors).length} rows had errors.
              errors: ${JSON.stringify(errors)}
              `,
          );
        }
      } catch (err) {
        alert('Something went wrong while processing the file.');
      } finally {
        setIsLoadingData(false);
      }
    };

    reader.readAsText(file);
  };
  const handleSave = () => {
    if (importData) mutateAsync(importData as any);
  };
  return (
    <>
      {modalOpen && (
        <div className="tag-ds">
          <Dialog
            fullScreen
            PaperProps={{
              style: {
                borderRadius: 8,
                width: '100%',
                maxWidth: '100%',
                height: '100%',
              },
            }}
            sx={{ maxWidth: '100%', width: '100%' }}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            fullWidth
            confirmLabel="Save"
            onConfirm={handleSave}
          >
            <div className="tag-ds App feature-dashboard">
              {isLoadingData && <Loader />}
              {!isLoadingData && importData && <MasterDataTable activeTab={acitveTab} dataSource={importData} />}
            </div>
          </Dialog>
        </div>
      )}
    </>
  );
}
const MasterDataImport = forwardRef(MasterDataImportRender) as (props: Props & { ref?: any }) => JSX.Element;

export default MasterDataImport;
